import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { loadGoogleMaps } from "@/lib/maps";
import { closeSuggestions, keepFieldVisible } from "@/lib/mobileView";
import { trackOrg } from "@/lib/track";

function AddressInput({ maps, value, onChange }: any) {
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let cancelled = false; let el: any = null; let unkeep: (() => void) | null = null;
    (async () => {
      try {
        const g = await loadGoogleMaps(maps.browser_key);
        const { PlaceAutocompleteElement } = await g.maps.importLibrary("places");
        if (cancelled || !host.current) return;
        el = new PlaceAutocompleteElement({ includedRegionCodes: ["us"] });
        el.setAttribute("data-testid", "fq-address-autocomplete");
        el.setAttribute("placeholder", "Start typing the property address");
        el.addEventListener("gmp-select", async (ev: any) => {
          const place = ev.placePrediction.toPlace();
          await place.fetchFields({ fields: ["formattedAddress", "location", "id", "addressComponents"] });
          const pc = (place.addressComponents || []).find((c: any) => c.types.includes("postal_code"));
          onChange({ formatted: place.formattedAddress, place_id: place.id, lat: place.location.lat(), lng: place.location.lng(), postal_code: pc?.shortText || null });
          closeSuggestions(el);
          trackOrg("field_place_select");
        });
        host.current.innerHTML = ""; host.current.appendChild(el); unkeep = keepFieldVisible(el);
      } catch (_) { if (!cancelled) setFailed(true); }
    })();
    return () => { cancelled = true; unkeep?.(); el?.remove(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-2">
      <div ref={host} className="min-h-[48px] [&_gmp-place-autocomplete]:w-full" />
      {failed && <p className="text-sm text-destructive" role="alert">Address lookup is unavailable right now.</p>}
      {value && (
        <div className="rounded-lg border bg-secondary/50 p-3 space-y-1.5" data-testid="fq-address-confirm">
          <p className="text-xs font-medium flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />Service address (edit to add a unit number)</p>
          <Input className="h-12 text-base" value={value.formatted} onChange={(e) => onChange({ ...value, formatted: e.target.value })} data-testid="fq-address-input" />
        </div>
      )}
    </div>
  );
}

export function DetailsForm({ cfg, initial, busy, onSave }: any) {
  const [f, setF] = useState({ name: initial?.name || "", phone: initial?.phone || "", email: initial?.email || "" });
  const [address, setAddress] = useState<any>(initial?.address || null);
  const [sid, setSid] = useState<string>(initial?.service_id || (cfg.services.length === 1 ? cfg.services[0].id : ""));
  const set = (k: string) => (e: any) => setF({ ...f, [k]: e.target.value });
  const ok = f.name.trim() && (f.email.trim() || f.phone.trim()) && address?.formatted?.trim().length >= 3 && sid;
  return (
    <div className="space-y-5" data-testid="fq-details-step">
      <div className="space-y-3">
        <p className="font-semibold">Homeowner</p>
        <div><Label className="text-xs">Full name</Label><Input className="h-12 text-base" value={f.name} onChange={set("name")} autoComplete="off" data-testid="fq-name-input" /></div>
        <div><Label className="text-xs">Phone</Label><Input className="h-12 text-base" type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} data-testid="fq-phone-input" /></div>
        <div><Label className="text-xs">Email (needed to send the estimate)</Label><Input className="h-12 text-base" type="email" inputMode="email" value={f.email} onChange={set("email")} data-testid="fq-email-input" /></div>
        <p className="text-[11px] text-muted-foreground">Enter at least an email or a phone number.</p>
      </div>
      <div className="space-y-2">
        <p className="font-semibold">Property address</p>
        <AddressInput maps={cfg.maps} value={address} onChange={setAddress} />
      </div>
      <div className="space-y-2" data-testid="fq-service-step">
        <p className="font-semibold">Service</p>
        {cfg.services.length === 0 && <p className="text-sm text-muted-foreground">Set a price on at least one service in Services &amp; Pricing first.</p>}
        {cfg.services.map((s: any) => (
          <button key={s.id} type="button" onClick={() => setSid(s.id)} aria-pressed={sid === s.id} data-testid={`fq-service-${s.id.slice(0, 8)}`}
            className={`w-full min-h-[56px] rounded-xl border px-4 py-3 text-left transition-colors ${sid === s.id ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-card"}`}>
            <span className="font-medium block">{s.name}{!s.active && <span className="ml-2 text-[10px] uppercase text-muted-foreground">Draft</span>}</span>
            <span className="text-xs text-muted-foreground">{s.pricing_unit === "linear_ft" ? "Measured in linear feet" : "Measured in square feet"}</span>
          </button>
        ))}
      </div>
      <Button size="lg" className="w-full h-14 text-base" disabled={!ok || busy} data-testid="fq-details-save-button"
        onClick={() => onSave({ name: f.name.trim(), phone: f.phone.trim() || null, email: f.email.trim() || null, service_id: sid, address: { ...address, formatted: address.formatted.trim() } })}>
        {busy ? "Saving…" : "Save & open satellite map"}
      </Button>
    </div>
  );
}
